import { useState, useEffect } from "react";

import { Loader2, TrendingUp, AlertTriangle, Target, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface CategoryAnalysis {
  category: string;
  contentCount: number;
  totalUsage: number;
  avgUsagePerItem: number;
  lastUpdated: string | null;
  gapScore: number;
  priority: "critical" | "high" | "medium" | "low";
  recommendation: string;
}

interface ContentTypeAnalysis {
  type: "Board Packs" | "Convention Packs" | "Session Frameworks";
  totalItems: number;
  totalCategories: number;
  mostPopularCategory: string;
  leastServedCategory: string;
  gaps: CategoryAnalysis[];
}

export const ContentGapAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [analyses, setAnalyses] = useState<ContentTypeAnalysis[]>([]);

  useEffect(() => {
    fetchGapAnalysis();
  }, []);

  const fetchGapAnalysis = async () => {
    setLoading(true);
    try {
      // Analyze Board Packs
      const { data: boards } = await supabase
        .from("prebuilt_boards")
        .select("*")
        .eq("status", "published");

      const { data: boardUsage } = await supabase
        .from("prebuilt_board_usage_stats")
        .select("*");

      // Analyze Convention Packs
      const { data: packs } = await supabase
        .from("convention_packs")
        .select("*");

      const { data: packUsage } = await supabase
        .from("convention_pack_usage_stats")
        .select("*");

      const { data: frameworks } = await supabase
        .from("session_frameworks")
        .select("*")
        .eq("status", "published");

      const results: ContentTypeAnalysis[] = [];

      // Analyze Board Packs (they don't have explicit categories, so we'll skip category analysis)
      results.push({
        type: "Board Packs",
        totalItems: boards?.length || 0,
        totalCategories: 0,
        mostPopularCategory: "N/A",
        leastServedCategory: "N/A",
        gaps: []
      });

      // Analyze Convention Packs
      if (packs && packUsage) {
        const packsByCategory = new Map<string, any[]>();
        packs.forEach(pack => {
          const cat = pack.category || "uncategorized";
          if (!packsByCategory.has(cat)) {
            packsByCategory.set(cat, []);
          }
          packsByCategory.get(cat)!.push(pack);
        });

        const categoryGaps: CategoryAnalysis[] = [];
        const allCategories = ["writing", "code_style", "documentation", "testing", "architecture", "security"];

        allCategories.forEach(category => {
          const items = packsByCategory.get(category) || [];
          const usage = packUsage.filter(u => u.category === category);
          const totalUsage = usage.reduce((sum, u) => sum + (u.total_uses || 0), 0);
          const avgUsage = items.length > 0 ? totalUsage / items.length : 0;
          
          const lastUpdated = items.length > 0 
            ? items.reduce((latest, item) => {
                const itemDate = new Date(item.updated_at || item.created_at);
                return itemDate > new Date(latest) ? itemDate.toISOString() : latest;
              }, items[0].updated_at || items[0].created_at)
            : null;

          // Calculate gap score (higher = more critical)
          let gapScore = 0;
          if (items.length === 0) gapScore += 100;
          if (items.length < 3) gapScore += 50;
          if (totalUsage > 50 && items.length < 5) gapScore += 30;
          if (lastUpdated && new Date(lastUpdated) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) gapScore += 20;

          let priority: CategoryAnalysis["priority"] = "low";
          if (gapScore >= 100) priority = "critical";
          else if (gapScore >= 70) priority = "high";
          else if (gapScore >= 40) priority = "medium";

          let recommendation = "";
          if (items.length === 0) {
            recommendation = `No content exists. Create ${category} conventions immediately.`;
          } else if (items.length < 3) {
            recommendation = `Only ${items.length} item(s). Add ${3 - items.length} more for variety.`;
          } else if (totalUsage > 100 && items.length < 5) {
            recommendation = `High demand (${totalUsage} uses) but limited selection. Expand offerings.`;
          } else if (lastUpdated && new Date(lastUpdated) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) {
            recommendation = "Last updated over 90 days ago. Consider refreshing content.";
          } else {
            recommendation = "Well-served category. Monitor usage trends.";
          }

          categoryGaps.push({
            category,
            contentCount: items.length,
            totalUsage,
            avgUsagePerItem: avgUsage,
            lastUpdated,
            gapScore,
            priority,
            recommendation
          });
        });

        categoryGaps.sort((a, b) => b.gapScore - a.gapScore);

        results.push({
          type: "Convention Packs",
          totalItems: packs.length,
          totalCategories: packsByCategory.size,
          mostPopularCategory: categoryGaps.reduce((max, gap) => 
            gap.totalUsage > (categoryGaps.find(g => g.category === max)?.totalUsage || 0) ? gap.category : max
          , categoryGaps[0]?.category || "N/A"),
          leastServedCategory: categoryGaps[categoryGaps.length - 1]?.category || "N/A",
          gaps: categoryGaps
        });
      }

      // Analyze Session Frameworks
      if (frameworks) {
        const frameworksByCategory = new Map<string, any[]>();
        frameworks.forEach(framework => {
          const cat = framework.category || "uncategorized";
          if (!frameworksByCategory.has(cat)) {
            frameworksByCategory.set(cat, []);
          }
          frameworksByCategory.get(cat)!.push(framework);
        });

        const categoryGaps: CategoryAnalysis[] = [];
        const allCategories = ["problem-solving", "strategy", "brainstorming", "decision-making", "planning", "review"];

        allCategories.forEach(category => {
          const items = frameworksByCategory.get(category) || [];
          // Note: No usage stats table exists yet for frameworks
          const totalUsage = 0;
          const avgUsage = 0;
          
          const lastUpdated = items.length > 0 
            ? items.reduce((latest, item) => {
                const itemDate = new Date(item.updated_at || item.created_at);
                return itemDate > new Date(latest) ? itemDate.toISOString() : latest;
              }, items[0].updated_at || items[0].created_at)
            : null;

          let gapScore = 0;
          if (items.length === 0) gapScore += 100;
          if (items.length < 2) gapScore += 50;
          if (totalUsage > 30 && items.length < 4) gapScore += 30;
          if (lastUpdated && new Date(lastUpdated) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) gapScore += 20;

          let priority: CategoryAnalysis["priority"] = "low";
          if (gapScore >= 100) priority = "critical";
          else if (gapScore >= 70) priority = "high";
          else if (gapScore >= 40) priority = "medium";

          let recommendation = "";
          if (items.length === 0) {
            recommendation = `No frameworks exist. Create ${category} framework immediately.`;
          } else if (items.length < 2) {
            recommendation = `Only ${items.length} framework. Add at least 1 more for options.`;
          } else if (totalUsage > 50 && items.length < 4) {
            recommendation = `High demand (${totalUsage} uses) but limited selection. Expand offerings.`;
          } else if (lastUpdated && new Date(lastUpdated) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) {
            recommendation = "Last updated over 90 days ago. Consider refreshing content.";
          } else {
            recommendation = "Well-served category. Monitor usage trends.";
          }

          categoryGaps.push({
            category,
            contentCount: items.length,
            totalUsage,
            avgUsagePerItem: avgUsage,
            lastUpdated,
            gapScore,
            priority,
            recommendation
          });
        });

        categoryGaps.sort((a, b) => b.gapScore - a.gapScore);

        results.push({
          type: "Session Frameworks",
          totalItems: frameworks.length,
          totalCategories: frameworksByCategory.size,
          mostPopularCategory: categoryGaps.reduce((max, gap) => 
            gap.totalUsage > (categoryGaps.find(g => g.category === max)?.totalUsage || 0) ? gap.category : max
          , categoryGaps[0]?.category || "N/A"),
          leastServedCategory: categoryGaps[categoryGaps.length - 1]?.category || "N/A",
          gaps: categoryGaps
        });
      }

      setAnalyses(results);
    } catch (error) {
      console.error("Gap analysis error:", error);
      toast.error("Failed to analyze content gaps");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: CategoryAnalysis["priority"]) => {
    switch (priority) {
      case "critical": return "destructive";
      case "high": return "default";
      case "medium": return "secondary";
      case "low": return "outline";
    }
  };

  const getPriorityIcon = (priority: CategoryAnalysis["priority"]) => {
    switch (priority) {
      case "critical": return <AlertTriangle className="h-4 w-4" />;
      case "high": return <TrendingUp className="h-4 w-4" />;
      case "medium": return <Target className="h-4 w-4" />;
      case "low": return <Sparkles className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Content Gap Analysis
            </CardTitle>
            <CardDescription>
              Identify underserved categories and prioritize content creation efforts
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchGapAnalysis}>
            Refresh Analysis
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {analyses.map((analysis) => (
          <div key={analysis.type} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{analysis.type}</h3>
              <div className="text-sm text-muted-foreground">
                {analysis.totalItems} items across {analysis.totalCategories} categories
              </div>
            </div>

            {analysis.gaps.length > 0 ? (
              <div className="space-y-3">
                {analysis.gaps.map((gap) => (
                  <Card key={gap.category}>
                    <CardContent className="pt-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold capitalize">{gap.category}</span>
                            <Badge variant={getPriorityColor(gap.priority)}>
                              <div className="flex items-center gap-1">
                                {getPriorityIcon(gap.priority)}
                                {gap.priority}
                              </div>
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{gap.contentCount} items</span>
                            <span>{gap.totalUsage} uses</span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Content Coverage</span>
                            <span>{Math.min(100, gap.contentCount * 20)}%</span>
                          </div>
                          <Progress value={Math.min(100, gap.contentCount * 20)} />
                        </div>

                        <p className="text-sm text-muted-foreground">{gap.recommendation}</p>

                        {gap.avgUsagePerItem > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Avg. {gap.avgUsagePerItem.toFixed(1)} uses per item
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No category analysis available for {analysis.type}
              </p>
            )}
          </div>
        ))}

        {analyses.every(a => a.gaps.length === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No significant content gaps detected</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
